/**
 * Rent and gross yield for a district, from REGA's published indicators.
 *
 * Two honesty rules are load-bearing here:
 *
 *  1. REGA publishes 0 when it is WITHHOLDING a figure, not when rent is free.
 *     Withheld cells never reach this component — they are dropped upstream in
 *     the engine's export — so anything rendered here is a real published
 *     average.
 *  2. The yield is gross. It is labelled gross, before costs and before the 5%
 *     real-estate transaction tax, every time it appears. A yield quoted without
 *     that line is a number an investor will hold against you.
 *
 * Unlike the sale card, this one CAN speak about villas and floors: REGA
 * publishes rent by its own property types, which the Ministry of Justice deed
 * data does not.
 */

import { useTranslation } from 'react-i18next';
import { formatNumber, formatPrice } from '../../utils/propertyUtils';
import type { MarketCard } from '../../types/marketData';
import MarketEmpty from './MarketEmpty';
import MarketSourceNote from './MarketSourceNote';

export interface MarketRentCardProps {
  card: MarketCard | null;
  className?: string;
}

export default function MarketRentCard({ card, className = '' }: MarketRentCardProps) {
  const { t } = useTranslation('marketData');

  if (!card || !card.available) return null;

  const { rent, yield: gross, place } = card;

  const shell = (children: React.ReactNode) => (
    // A block inside the property page's «السوق في هذا الحي» section (no box of its own).
    <section className={className}>
      {children}
    </section>
  );

  const header = (
    <header className="mb-4">
      <h3 className="text-base font-semibold text-[var(--pv-color-text)]">
        {t('rent.title', { defaultValue: 'Rent in this district' })}
      </h3>
      <p className="mt-1 text-[13px] text-[var(--pv-color-text-muted)]">
        {place.districtAr} · {place.cityAr}
      </p>
    </header>
  );

  if (!rent.length) {
    return shell(
      <>
        {header}
        <MarketEmpty kind="not-enough-deals" />
      </>,
    );
  }

  const freeze = rent.some((r) => r.freeze_note);

  return shell(
    <>
      {header}

      <ul className="divide-y divide-[var(--pv-border-subtle)]">
        {rent.map((r) => (
          <li key={r.type} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--pv-color-text)]">
                {t(`rent.types.${r.type}`, { defaultValue: r.type })}
              </p>
              <p className="mt-0.5 text-[12px] text-[var(--pv-color-text-subtle)]">
                {t('rent.contracts', { count: r.n, defaultValue: '{{count}} contracts' })}
              </p>
            </div>
            <div className="text-end">
              <p className="text-base font-semibold tabular-nums text-[var(--pv-color-text)]">
                {formatPrice(r.avg_year)}
              </p>
              <p className="mt-0.5 text-[12px] tabular-nums text-[var(--pv-color-text-muted)]">
                {formatNumber(r.per_m2)} {t('rent.perM2Year', { defaultValue: 'SAR / m² a year' })}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {gross && (
        <div className="mt-4 rounded-brand-md border border-[var(--pv-border-subtle)] bg-[var(--pv-color-surface-soft)] p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-[var(--pv-color-text)]">
              {t('yieldCard.gross', { defaultValue: 'Gross yield' })}
            </span>
            <span className="text-xl font-semibold tabular-nums text-[var(--pv-color-primary)]">
              {(gross.gross_m2 * 100).toFixed(1)}%
            </span>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-[var(--pv-color-text-muted)]">
            {t('yieldCard.note', {
              defaultValue: 'Gross, before costs and the 5% real-estate transaction tax',
            })}
            {!gross.solid
              ? ` · ${t('yieldCard.indicative', { defaultValue: 'Indicative - small sample' })}`
              : ''}
          </p>
        </div>
      )}

      {freeze && (
        <p className="mt-3 rounded-brand-sm border border-[#FBE5A3] bg-[var(--pv-color-gold-soft)] p-3 text-[12px] leading-relaxed text-[#8C6A0B]">
          {t('rent.freezeNotice', { defaultValue: 'Riyadh rents are subject to a freeze order.' })}
        </p>
      )}

      <MarketSourceNote sale={false} rent updatedAt={card.generatedAt} className="mt-5" />
    </>,
  );
}
